import { Badge } from 'antd';
import React from 'react';
import { useTranslation } from 'react-i18next';
import FileIcon from '../../Common/FileIcon';
import type File from '../../../interfaces/File';
import { FILE_PREVIEW_EXTENSIONS_IMAGES } from '../../../utils/utils';

interface FilesGhostCardProps {
  files: File[];
}

/**
 * FilesGhostCard displays a floating "ghost" card UI for drag operations,
 * showing file icon(s) and associated details.
 */
const FilesGhostCard: React.FC<FilesGhostCardProps> = ({ files }) => {
  const { t } = useTranslation();
  const filesCount = files.length;
  const firstFile = files[0];

  // Determine title for the ghost card: either file count or filename
  const title =
    filesCount > 1
      ? `${filesCount} ${t('common.items')}`
      : (firstFile?.fileName ?? t('common.item'));

  // Determine subtitle: show filename for multi-select or show file/folder text for single
  const subtitle =
    filesCount > 1
      ? (firstFile?.fileName ?? t('common.file'))
      : firstFile?.isFolder
        ? t('files.moveFolder', 'Move folder')
        : t('files.moveFile', 'Move file');

  return (
    <div
      // Ghost card container is positioned far offscreen by default (will be moved in drag handler)
      className="z-drag-preview pointer-events-none fixed font-sans"
      style={{ top: -10000, left: -10000 }}
    >
      <div
        data-ghost-card-root
        className="border-gray-medium flex max-w-[360px] min-w-[200px] items-center gap-2.5 rounded-md border bg-white px-3 py-2.5 shadow-sm"
      >
        {/* File thumbnail(s) stack */}
        <div className="relative h-9 w-9">
          {firstFile && (
            <div className="absolute top-0 left-0 z-10 flex h-8 w-8">
              <FileIcon
                allowedFilePreviewExtensions={FILE_PREVIEW_EXTENSIONS_IMAGES}
                fileId={firstFile.id}
                className="h-8 w-8"
                fileExtension={firstFile.fileExtension}
                secretPreset={firstFile.secretPreset}
                isFolder={firstFile.isFolder}
              />
            </div>
          )}
          {files.length > 1 && (
            <div className="absolute -top-1 -right-1 h-8 w-8 rotate-12">
              <FileIcon
                allowedFilePreviewExtensions={FILE_PREVIEW_EXTENSIONS_IMAGES}
                fileId={files?.[1]?.id}
                className="h-8 w-8"
                fileExtension={files[1]?.fileExtension ?? ''}
                secretPreset={files[1]?.secretPreset}
                isFolder={files[1]?.isFolder ?? false}
              />
            </div>
          )}
        </div>

        {/* File/folder name and description */}
        <div className="flex min-w-0 flex-col">
          <div className="text-blue-dark max-w-[220px] truncate text-sm font-semibold">{title}</div>
          <div className="text-gray-dark max-w-[240px] truncate text-xs">{subtitle}</div>
        </div>

        {/* Badge displays the number of files if multi-select */}
        {filesCount > 1 && <Badge count={filesCount} className="absolute -top-2 -right-2" />}
      </div>
    </div>
  );
};

export default FilesGhostCard;
